import React, { useCallback } from "react";
import moment from "moment";

import { TransferableAddress, TransferablePatientInfoResponse, TransferablePhone } from "@/Common/Api/ExternalCommunications/Dto/TransferablePatientInfoResponse";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { Patient } from "@/Common/Models/Patient/Patient";

import styles from "./PatientsFoundResult.module.scss";

import img_p_banner_icon_100 from "@/Globals/_images/p-banner-icon-100.png";

interface IPatientsFoundResultProps {
    isLoading: boolean;
    originalPatient?: Patient;
    patients: TransferablePatientInfoResponse[];
    selectedPatient: TransferablePatientInfoResponse;
    onPatientSelected(patient: TransferablePatientInfoResponse): void
}
const getLookupValueIfAvailable = (code: string, typeCode: string): string | null => {
    if (!code || !typeCode) return null;
    const lookup = cacheStore.FindLookupByLookupCode(code, typeCode);
    return lookup?.GetValue() || null;
};

const PatientsFoundResult: React.FC<IPatientsFoundResultProps> = ({ isLoading, originalPatient, patients, selectedPatient, onPatientSelected }) => {
    const HIGHTLIGHT_CLASS = "highlight-yellow";

    const isSameLastName = useCallback((patient: TransferablePatientInfoResponse) => {
        if (originalPatient == null) {
            return false;
        }
        else if (!originalPatient.IndividualResponse.LastName || !patient.LastName)
            return false;

        return originalPatient.IndividualResponse.LastName.toLowerCase() === patient.LastName.toLowerCase();
    }, [originalPatient]);

    const isSameFirstName = useCallback((patient: TransferablePatientInfoResponse) => {
        if (originalPatient == null) {
            return false;
        }
        else if (!originalPatient.IndividualResponse.FirstName || !patient.FirstName)
            return false;

        return originalPatient.IndividualResponse.FirstName.toLowerCase() === patient.FirstName.toLowerCase();
    }, [originalPatient]);

    const doesPhoneExist = useCallback((phone: string, patient: TransferablePatientInfoResponse) => {
        if (!phone || isArrayNullOrUndefinedOrEmpty(patient.Phones)) {
            return false;
        }
        if (originalPatient == null) {
            return false;
        }
        else
            return originalPatient.Phones.any(item => item.Number === phone);
    }, [originalPatient]);

    const doesAddressExist = useCallback((address: string, patient: TransferablePatientInfoResponse) => {
        if (!address || isArrayNullOrUndefinedOrEmpty(patient.Addresses)) {
            return false;
        }
        if (originalPatient == null) {
            return false;
        }
        else
            return originalPatient.Addresses.any(x => `${x.StreetAddress || ""}, ${x.City || ""}` === address);
    }, [originalPatient]);

    const isSameHealthCard = useCallback((patient: TransferablePatientInfoResponse) => {
        if (originalPatient == null) {
            return false;
        }
        else if (!originalPatient.IndividualResponse.NAM || !patient.HealthIdentifiers[0].Value)
            return false;
        return originalPatient.IndividualResponse.NAM === patient.HealthIdentifiers[0].Value;
    }, [originalPatient]);

    const isSameDOB = useCallback((patient: TransferablePatientInfoResponse) => {
        if (originalPatient == null) {
            return false;
        }
        else if (!originalPatient.IndividualResponse.DateOfBirth || !patient.DateOfBirth)
            return false;
        return moment(originalPatient.IndividualResponse.DateOfBirth).isSame(moment(patient.DateOfBirth), "day");
    }, [originalPatient]);

    const sortedPatients = React.useMemo(() => patients.sort((a, b) => {
        const nbOfSamePropertiesA = [isSameLastName(a), isSameFirstName(a),
            doesPhoneExist(a.Phones.map(phone => phone.PhoneNumber).toString(), a),
            doesAddressExist(a.Addresses.map(address => `${address.StreetAddress || ""}, ${address.City || ""}`).toString(), a), isSameHealthCard(a), isSameDOB(a)].filter(v => !!v);

        const nbOfSamePropertiesB = [isSameLastName(b), isSameFirstName(b),
            doesPhoneExist(a.Phones.map(phone => phone.PhoneNumber).toString(), b),
            doesAddressExist(a.Addresses.map(address => `${address.StreetAddress || ""}, ${address.City || ""}`).toString(), b), isSameHealthCard(b), isSameDOB(b)].filter(v => !!v);

        if (nbOfSamePropertiesA === nbOfSamePropertiesB) {
            return `${a.FirstName}${a.LastName}`.localeCompare(`${b.FirstName}${b.LastName}`);
        }
        return nbOfSamePropertiesB.length - nbOfSamePropertiesA.length;
    }), [doesAddressExist, doesPhoneExist, isSameDOB, isSameFirstName, isSameHealthCard, isSameLastName, patients]);

    const renderPatientFound = useCallback((patient: TransferablePatientInfoResponse): JSX.Element => {
        let dobString = patient.DateOfBirth ? moment(patient.DateOfBirth).format("YYYY/MM/DD") : "";
        let ageString = dobString ? `(${PatientHelper.getFormattedAge(new Date(dobString))})` : "";

        let healthCard = !isArrayNullOrUndefinedOrEmpty(patient.HealthIdentifiers) ? patient.HealthIdentifiers[0].Value : "";

        const isSelected = selectedPatient?.ContactId === patient?.ContactId;
        const hasAddresses = patient.Addresses?.length > 0;

        const phoneList = patient.Phones.map((phone: TransferablePhone, i: number) => (
            <div className={styles.element} key={i + "__npl"}>
                <span className={`${styles.icon} myle-icon-16 phone`} />
                <div>
                    <span className={doesPhoneExist(phone.PhoneNumber, patient) ? HIGHTLIGHT_CLASS : ""}>
                        {phone.Country && MyleHelper.FormatPhone(phone.PhoneNumber, phone.Country?.Code)}
                        &nbsp;
                        ({phone.ContactType && getLookupValueIfAvailable(phone.ContactType?.Code, phone.ContactType?.TypeCode)} - {phone.Type && getLookupValueIfAvailable(phone.Type?.Code, phone.Type?.TypeCode)})
                    </span>
                </div>
            </div>

        ));

        const address = patient.Addresses.map((address: TransferableAddress, i) => (
            <span key={i + "__addr"} className={doesAddressExist(`${address.StreetAddress || ""}, ${address.City || ""}`, patient) ? HIGHTLIGHT_CLASS : ""}>
                {address.StreetAddress}, {address.City}, {address.Region && getLookupValueIfAvailable(address.Region?.Code, address.Region?.TypeCode)}, {address.PostalCode}, {address.Country && getLookupValueIfAvailable(address.Country?.Code, address.Country?.TypeCode)}
            </span>
        ));

        return (
            <div key={patient.ContactId + "__patient-id"} className={`pointer ${isSelected ? styles.selected : ""}`} onClick={() => onPatientSelected(patient)}>
                <div className={styles.patient}>
                    <div className={styles.logo}><img src={img_p_banner_icon_100} alt="MYLE" width="48" /></div>
                    <div className={styles.info}>
                        <div className={styles.left}>
                            <div>
                                <h4 className={`${styles.h4} light-blue`}> <span className={isSameLastName(patient) ? HIGHTLIGHT_CLASS : ""}>{patient.LastName}, </span>
                                    <span className={isSameFirstName(patient) ? HIGHTLIGHT_CLASS : ""}> {patient.FirstName}</span>
                                </h4>
                                {!!patient?.Gender?.Code ? <span className={`myle-icon-16 ${patient?.Gender?.Code.toLowerCase()}`} /> : null}
                            </div>
                            <div><strong>{cacheStore.GetString("HealthCard")}</strong> <span className={isSameHealthCard(patient) ? HIGHTLIGHT_CLASS : ""}>{healthCard}</span></div>
                            <div><strong>{cacheStore.GetString("DateofBirthLabel")}</strong> <span className={isSameDOB(patient) ? HIGHTLIGHT_CLASS : ""}> {dobString} {ageString}</span></div>
                        </div>
                        <div className={styles.right}>
                            {phoneList}

                            {hasAddresses && (
                                <div className={styles.element}>
                                    <span className={`${styles.icon} myle-icon-16 pin`} />
                                    <div>{address}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [doesAddressExist, doesPhoneExist, isSameDOB, isSameFirstName, isSameHealthCard, isSameLastName, onPatientSelected, selectedPatient?.ContactId]);

    const patientList = React.useMemo(() => sortedPatients.map(p => renderPatientFound(p)), [sortedPatients, renderPatientFound]);

    return (
        <div data-hvar="40" className={`scrollable ${styles.list} ${originalPatient == null ? styles.fullWidth : styles.partialWidth}`}>
            {patientList}
            {!isLoading && !patientList.any() && (
                <div className={`center ${styles.notFound}`}>
                    {cacheStore.GetString("PatientImportDialogNotFoundLabel")}
                </div>
            )}
        </div>
    );
};

export { PatientsFoundResult };
